import React, { useState, useEffect } from 'react';
import {
    TextField,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    FormControl,
    FormHelperText,
    Box,
    Chip,
    Stack,
    InputLabel,
    WarningIcon
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

/**
 * Helper function to validate a value against schema constraints
 * Returns validation error message if value violates constraints, null otherwise
 */
const validateValueAgainstSchema = (value, validation, fieldType) => {
    if (!validation || typeof validation !== 'object') {
        return null;
    }

    // Check minimum constraint
    if (validation.minimum !== undefined && typeof value === 'number') {
        if (value < validation.minimum) {
            return `must be at least ${validation.minimum}`;
        }
    }

    // Check maximum constraint
    if (validation.maximum !== undefined && typeof value === 'number') {
        if (value > validation.maximum) {
            return `must be at most ${validation.maximum}`;
        }
    }

    // Check minLength constraint
    if (validation.minLength !== undefined && typeof value === 'string') {
        if (value.length < validation.minLength) {
            return `must be at least ${validation.minLength} character(s)`;
        }
    }

    // Check maxLength constraint
    if (validation.maxLength !== undefined && typeof value === 'string') {
        if (value.length > validation.maxLength) {
            return `must be at most ${validation.maxLength} character(s)`;
        }
    }

    // Check minItems constraint
    if (validation.minItems !== undefined && Array.isArray(value)) {
        if (value.length < validation.minItems) {
            return `must have at least ${validation.minItems} item(s)`;
        }
    }

    // Check maxItems constraint
    if (validation.maxItems !== undefined && Array.isArray(value)) {
        if (value.length > validation.maxItems) {
            return `must have at most ${validation.maxItems} item(s)`;
        }
    }

    // Check date field is provided (for datepicker type)
    if (fieldType === 'datepicker' && validation.minLength !== undefined) {
        if (!value || value === '' || value === null) {
            return 'a date is required';
        }
    }

    return null;
};

/**
 * JobFormField Component
 * Reusable form field component that renders different Material-UI input types
 * based on field schema (TextField, Select, Checkbox, DatePicker, etc.)
 */
const JobFormField = ({
    fieldName,
    fieldSchema,
    value,
    onChange,
    error,
    disabled = false,
    fullWidth = true
}) => {
    // Local state for array input to handle typing commas properly
    const [arrayInputValue, setArrayInputValue] = useState('');

    // Sync array input value when the parent value changes
    useEffect(() => {
        if (fieldSchema?.type === 'array') {
            const arrayValue = Array.isArray(value) ? value : [];
            setArrayInputValue(arrayValue.join(', '));
        }
    }, [value, fieldSchema?.type]);

    if (!fieldSchema) {
        return null;
    }

    const {
        label,
        type,
        help,
        required,
        validation = {},
        options = []
    } = fieldSchema;

    // Check for validation errors against schema constraints
    const validationError = validateValueAgainstSchema(value, validation, type);
    const hasError = !!error || !!validationError;
    const errorMessage = error || validationError || '';

    // Handle hidden fields
    if (type === 'hidden') {
        return null;
    }

    // Handle text input
    if (type === 'text') {
        return (
            <TextField
                fullWidth={fullWidth}
                label={label}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                error={hasError}
                helperText={errorMessage || help}
                required={required}
                disabled={disabled}
                variant="outlined"
                size="small"
                sx={{
                    marginBottom: '1rem'
                }}
            />
        );
    }

    // Handle number input
    if (type === 'number') {
        return (
            <Box sx={{ marginBottom: '1rem', position: 'relative' }}>
                <TextField
                    fullWidth={fullWidth}
                    label={label}
                    type="number"
                    value={value !== undefined && value !== null ? value : ''}
                    onChange={(e) => {
                        const numValue = e.target.value === '' ? '' : parseFloat(e.target.value);
                        onChange(numValue);
                    }}
                    error={hasError}
                    helperText={errorMessage || help}
                    required={required}
                    disabled={disabled}
                    variant="outlined"
                    size="small"
                    inputProps={{
                        min: validation.minimum,
                        max: validation.maximum,
                        step: 'any'
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            ...(hasError && {
                                '& fieldset': {
                                    borderColor: '#d32f2f',
                                    borderWidth: '2px'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#d32f2f'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#d32f2f'
                                }
                            })
                        }
                    }}
                />
                {hasError && (
                    <WarningAmberIcon
                        sx={{
                            position: 'absolute',
                            right: '12px',
                            top: '16px',
                            color: '#d32f2f',
                            fontSize: '1.5rem'
                        }}
                    />
                )}
            </Box>
        );
    }

    // Handle dropdown/select
    if (type === 'dropdown') {
        return (
            <FormControl
                fullWidth={fullWidth}
                error={hasError}
                disabled={disabled}
                size="small"
                sx={{
                    marginBottom: '1rem'
                }}
            >
                <InputLabel>{label}</InputLabel>
                <Select
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label={label}
                >
                    {options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
                {(errorMessage || help) && (
                    <FormHelperText>{errorMessage || help}</FormHelperText>
                )}
            </FormControl>
        );
    }

    // Handle checkbox
    if (type === 'checkbox') {
        return (
            <Box sx={{ marginBottom: '1rem' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={value || false}
                            onChange={(e) => onChange(e.target.checked)}
                            disabled={disabled}
                        />
                    }
                    label={label}
                />
                {(errorMessage || help) && (
                    <FormHelperText error={hasError}>
                        {errorMessage || help}
                    </FormHelperText>
                )}
            </Box>
        );
    }

    // Handle date picker
    if (type === 'datepicker') {
        return (
            <Box sx={{ marginBottom: '1rem', position: 'relative' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={label}
                        value={value ? dayjs(value) : null}
                        onChange={(newValue) => {
                            onChange(newValue ? newValue.format('YYYY-MM-DD') : '');
                        }}
                        disabled={disabled}
                        slotProps={{
                            textField: {
                                fullWidth: fullWidth,
                                error: hasError,
                                helperText: errorMessage || help,
                                required: required,
                                size: 'small',
                                variant: 'outlined',
                                sx: {
                                    '& .MuiOutlinedInput-root': {
                                        ...(hasError && {
                                            '& fieldset': {
                                                borderColor: '#d32f2f',
                                                borderWidth: '2px'
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#d32f2f'
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#d32f2f'
                                            }
                                        })
                                    }
                                }
                            }
                        }}
                    />
                </LocalizationProvider>
                {hasError && (
                    <WarningAmberIcon
                        sx={{
                            position: 'absolute',
                            right: '12px',
                            top: '16px',
                            color: '#d32f2f',
                            fontSize: '1.5rem'
                        }}
                    />
                )}
            </Box>
        );
    }

    // Handle array input (comma-separated)
    if (type === 'array') {
        const arrayValue = Array.isArray(value) ? value : [];

        const handleArrayInputChange = (e) => {
            // Update local state immediately to allow typing commas
            setArrayInputValue(e.target.value);
        };

        const handleArrayInputBlur = () => {
            // Convert to array and update parent on blur
            const arrayItems = arrayInputValue
                .split(',')
                .map((item) => item.trim())
                .filter((item) => item.length > 0);
            onChange(arrayItems);
        };

        return (
            <Box sx={{ marginBottom: '1rem', position: 'relative' }}>
                <TextField
                    fullWidth={fullWidth}
                    label={label}
                    placeholder="Enter items separated by commas"
                    value={arrayInputValue}
                    onChange={handleArrayInputChange}
                    onBlur={handleArrayInputBlur}
                    error={hasError}
                    helperText={errorMessage || help}
                    required={required}
                    disabled={disabled}
                    variant="outlined"
                    size="small"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            ...(hasError && {
                                '& fieldset': {
                                    borderColor: '#d32f2f',
                                    borderWidth: '2px'
                                },
                                '&:hover fieldset': {
                                    borderColor: '#d32f2f'
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#d32f2f'
                                }
                            })
                        }
                    }}
                />
                {hasError && (
                    <WarningAmberIcon
                        sx={{
                            position: 'absolute',
                            right: '12px',
                            top: '16px',
                            color: '#d32f2f',
                            fontSize: '1.5rem'
                        }}
                    />
                )}
                {arrayValue.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        {arrayValue.map((item, index) => (
                            <Chip
                                key={index}
                                label={item}
                                onDelete={() => {
                                    const newArray = arrayValue.filter((_, i) => i !== index);
                                    onChange(newArray);
                                }}
                                size="small"
                                sx={{ marginBottom: '0.25rem' }}
                            />
                        ))}
                    </Stack>
                )}
            </Box>
        );
    }

    // Handle object input (nested fields)
    if (type === 'object') {
        // Objects are handled by parent component, not rendered here
        return null;
    }

    // Fallback for unknown types
    return (
        <TextField
            fullWidth={fullWidth}
            label={label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            error={hasError}
            helperText={errorMessage || help}
            required={required}
            disabled={disabled}
            variant="outlined"
            size="small"
            sx={{
                marginBottom: '1rem'
            }}
        />
    );
};

export default JobFormField;
